// frontend/src/pages/AssignPoints/ReasonSelectPage.js
// Unified reason picker for individual students, rosters, and houses.
// Accepts IDs via:
//   - location.state.studentIds (array)
//   - ?student_ids=1,2,3 or ?student_id=1
//   - ?roster_id=123  → fetch roster members on demand
//   - ?house_name=Gryffindor → fetch students in that house
//
// On submit: POST /api/points/award/ once per student ID, then refresh leaderboard and go to /dashboard.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import constants from "../../data/constants.json";
import { api } from "../../api/client";
import { studentsApi } from "../../api/studentsApi";

const habitReasonMap = constants.habits || {};
const habitColors = constants.habitColors || {};

export default function ReasonSelectPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sp] = useSearchParams();

  // --- Resolve student IDs from multiple possible inputs
  const qsOne = sp.get("student_id");
  const qsMany = sp.get("student_ids");
  const rosterId = sp.get("roster_id");
  const houseName = (sp.get("house_name") || "").trim(); // optional

  // Extract state ids without depending on the whole location.state object
  const stateStudentIds = location?.state?.studentIds;

  const [rosterIds, setRosterIds] = useState([]);
  const [loadingRoster, setLoadingRoster] = useState(false);

  const [houseIds, setHouseIds] = useState([]);
  const [loadingHouse, setLoadingHouse] = useState(false);

  const studentIds = useMemo(() => {
    const stateIds = Array.isArray(stateStudentIds) ? stateStudentIds : [];

    if (stateIds.length) return stateIds.map(String);
    if (qsMany) {
      return String(qsMany)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
    if (qsOne) return [String(qsOne)];
    if (rosterId) return rosterIds.map(String); // once loaded
    if (houseName) return houseIds.map(String); // once loaded
    return [];
  }, [stateStudentIds, qsOne, qsMany, rosterId, rosterIds, houseName, houseIds]);

  // If roster_id present, fetch its students once
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!rosterId) return;
      setLoadingRoster(true);
      try {
        // Try a few common endpoints first
        const tries = [
          `/api/rosters/${rosterId}/students`,
          `/api/groups/${rosterId}/students`,
          `/api/rosters/${rosterId}`,
          `/api/groups/${rosterId}`,
        ];
        for (const url of tries) {
          const res = await api.get(url);
          if (res && res.ok) {
            const list = Array.isArray(res.data?.results)
              ? res.data.results
              : Array.isArray(res.data?.students)
              ? res.data.students
              : Array.isArray(res.data)
              ? res.data
              : [];
            const ids = list
              .map((r) => r.id || r.scholar_id || r.student_id)
              .filter(Boolean);
            if (ids.length) {
              if (alive) setRosterIds(ids);
              break;
            }
          }
        }
        // Fallback: generic list by roster_id
        if (alive && rosterIds.length === 0) {
          const listRes = await studentsApi.list({ roster_id: rosterId });
          if (listRes?.ok) {
            const ids = (listRes.students || listRes.raw?.results || listRes.raw || [])
              .map((s) => s.id || s.scholar_id || s.student_id)
              .filter(Boolean);
            setRosterIds(ids);
          }
        }
      } finally {
        if (alive) setLoadingRoster(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [rosterId, rosterIds.length]);

  // If house_name present, fetch its students once
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!houseName) return;
      setLoadingHouse(true);
      try {
        // 1) Prefer studentsApi helper (supports various backends internally)
        //    Try both common param keys to be robust across backends.
        const attempts = [
          () => studentsApi.list({ house: houseName }),
          () => studentsApi.list({ house_name: houseName }),
        ];

        let collectedIds = [];
        for (const fn of attempts) {
          try {
            const rep = await fn();
            if (rep?.ok) {
              const fromRep = (rep.students || rep.raw?.results || rep.raw || [])
                .map((s) => s.id || s.scholar_id || s.student_id)
                .filter(Boolean);
              if (fromRep.length) {
                collectedIds = fromRep;
                break;
              }
            }
          } catch (_) {
            // ignore and fall through to next attempt
          }
        }

        // 2) Fallback to direct endpoints if needed
        if (collectedIds.length === 0) {
          const directUrls = [
            `/api/students?house_name=${encodeURIComponent(houseName)}`,
            `/api/scholars?house_name=${encodeURIComponent(houseName)}`,
          ];
          for (const url of directUrls) {
            try {
              const res = await api.get(url);
              if (res?.ok) {
                const list = Array.isArray(res.data?.results)
                  ? res.data.results
                  : Array.isArray(res.data?.students)
                  ? res.data.students
                  : Array.isArray(res.data)
                  ? res.data
                  : [];
                const ids = list
                  .map((s) => s.id || s.scholar_id || s.student_id)
                  .filter(Boolean);
                if (ids.length) {
                  collectedIds = ids;
                  break;
                }
              }
            } catch (_) {
              // try next
            }
          }
        }

        if (alive) setHouseIds(collectedIds);
      } finally {
        if (alive) setLoadingHouse(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [houseName]);

  // --- Habit & reason selection UI state
  const [selectedHabit, setSelectedHabit] = useState("");
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [comment, setComment] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [err, setErr] = useState("");

  const abortRef = useRef(null);

  useEffect(() => {
    const firstHabit = Object.keys(habitReasonMap)[0] || "";
    setSelectedHabit(firstHabit);
  }, []);

  const reasonsForHabit = useMemo(
    () => (selectedHabit ? habitReasonMap[selectedHabit] || [] : []),
    [selectedHabit]
  );

  function toggleReason(reason) {
    setErr("");
    setSelectedReasons((prev) =>
      prev.includes(reason) ? prev.filter((r) => r !== reason) : [...prev, reason]
    );
  }

  const brand = habitColors[selectedHabit] || "#003e73";

  // Build the free-text "reason" to satisfy backend schema
  function composeReasonText() {
    const reasons = selectedReasons.join("; ");
    const base = `Habit: ${selectedHabit || "—"} — Reasons: ${reasons || "—"}`;
    return comment ? `${base}  Comment: ${comment}` : base;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setErr("");

    if (!selectedHabit) return setErr("Pick a habit.");
    if (selectedReasons.length === 0) return setErr("Pick at least one reason.");
    if (studentIds.length === 0) {
      return setErr(
        rosterId
          ? "No students in this roster."
          : houseName
          ? "No students in this house."
          : "No student selected."
      );
    }

    setSubmitting(true);
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const reasonText = composeReasonText();

      // Backend auto-fills house from scholar. Value = +1 by default here.
      for (const sid of studentIds) {
        const body = {
          scholar_id: Number(sid), // backend expects integer
          value: 1, // positive = award
          reason: reasonText, // free text
          source: "manual",
          term: "",
          // Optional: attach house if it was chosen earlier in flow
          ...(houseName ? { house_name: houseName } : {}),
        };
        const res = await api.post("/api/points/award/", body, {
          signal: controller.signal,
        });
        if (!res.ok) {
          const msg =
            (res.data && (res.data.detail || res.data.message || res.data.error)) ||
            `Award failed for student ${sid}.`;
          throw new Error(msg);
        }
      }

      // Kick the leaderboard so totals are fresh
      await api.get("/api/leaderboard/").catch(() => {});
      setShowSuccess(true);

      // Small pause for toast, then back to student select so the educator
      // can award more points without re-navigating from the dashboard.
      setTimeout(() => navigate("/assign/students"), 900);
    } catch (e) {
      setErr(e?.message || "Failed to submit awards.");
    } finally {
      setSubmitting(false);
    }
  }

  function cancelSubmit() {
    abortRef.current?.abort?.();
    setSubmitting(false);
  }

  return (
    <div style={s.page}>
      <h1 style={s.h1}>Select Reason(s)</h1>

      {/* Habit pills */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        {Object.keys(habitReasonMap).map((name) => (
          <button
            key={name}
            onClick={() => setSelectedHabit(name)}
            style={{
              ...s.pill,
              background: selectedHabit === name ? brand : "#f1f5f9",
              color: selectedHabit === name ? "#fff" : "#003e73",
              borderColor: selectedHabit === name ? brand : "#cbd5e1",
            }}
          >
            {name}
          </button>
        ))}
      </div>

      {/* Reasons */}
      <div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
          <label>Reason(s)</label>
          {selectedReasons.length > 0 && (
            <span style={{ fontSize: 12, color: "#666" }}>
              {selectedReasons.length} selected
            </span>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {reasonsForHabit.map((r) => (
            <button
              key={r}
              onClick={() => toggleReason(r)}
              style={{
                ...s.pill,
                background: selectedReasons.includes(r) ? brand : "#fff",
                color: selectedReasons.includes(r) ? "#fff" : "#003e73",
                borderColor: selectedReasons.includes(r) ? brand : "#94a3b8",
              }}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      {/* Optional comment */}
      <div style={{ marginTop: 12 }}>
        <label htmlFor="comment">Comment (optional)</label>
        <textarea
          id="comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Add context…"
          style={{
            width: "100%",
            minHeight: 80,
            borderRadius: 8,
            padding: 8,
            border: "1px solid #cbd5e1",
          }}
        />
      </div>

      {/* Submit */}
      <form onSubmit={handleSubmit} style={{ marginTop: 16 }}>
        {err && <div style={s.error}>{err}</div>}
        {loadingRoster && rosterId && <div>Loading roster students…</div>}
        {loadingHouse && houseName && <div>Loading house students…</div>}

        <button
          type="submit"
          disabled={submitting || (rosterId && loadingRoster) || (houseName && loadingHouse)}
          style={s.btn}
        >
          {submitting
            ? "Submitting…"
            : `Submit 1 Point to ${studentIds.length} ${
                studentIds.length === 1 ? "Student" : "Students"
              }`}
        </button>
        {submitting && (
          <button type="button" onClick={cancelSubmit} style={s.btnSecondary}>
            Cancel
          </button>
        )}
      </form>

      {/* Success toast */}
      {showSuccess && (
        <div
          role="status"
          aria-live="polite"
          style={{
            marginTop: 12,
            background: "#E9F6EC",
            border: "1px solid #B7E0C0",
            color: "#2E7D32",
            borderRadius: 8,
            padding: "10px 12px",
          }}
        >
          ✅ Points awarded successfully!
        </div>
      )}
    </div>
  );
}

const s = {
  page: { padding: 16 },
  h1: { margin: 0, marginBottom: 12, color: "#003e73" },
  pill: {
    border: "1px solid #cbd5e1",
    padding: "6px 10px",
    borderRadius: 999,
    cursor: "pointer",
    fontWeight: 600,
  },
  btn: {
    height: 40,
    padding: "0 16px",
    border: "none",
    borderRadius: 8,
    background: "#2e7d32",
    color: "#fff",
    cursor: "pointer",
  },
  btnSecondary: {
    height: 40,
    padding: "0 16px",
    borderRadius: 8,
    border: "1px solid rgba(0,0,0,0.2)",
    background: "#fff",
    color: "#333",
    cursor: "pointer",
    marginLeft: 8,
  },
  error: {
    color: "#b00020",
    background: "#FFEAEA",
    border: "1px solid #F5B5B5",
    borderRadius: 8,
    padding: "8px 10px",
    marginBottom: 10,
  },
};
